import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { CheckCircle2, Loader2, Check } from 'lucide-react'
import { formatCpfCnpj, formatPhone, formatCep } from '../../utils/masks'
import { fetchCEP } from '../../utils/validators'

const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8001',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
})

const MASK_NATIVO = {
  cpf_cnpj: formatCpfCnpj,
  telefone: formatPhone,
  cep: formatCep,
}

function chunk(arr, size) {
  if (!size || size <= 0 || size >= arr.length) return arr.length ? [arr] : []
  const paginas = []
  for (let i = 0; i < arr.length; i += size) paginas.push(arr.slice(i, i + size))
  return paginas
}

export default function CadastroPublicoPage() {
  const { slug } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [step, setStep] = useState(0)
  const [form, setForm] = useState({ nome: '', respostas: {}, aceite_lgpd: false, website: '' })
  const [enviando, setEnviando] = useState(false)
  const [enviado, setEnviado] = useState(false)
  const [mensagemSucesso, setMensagemSucesso] = useState('')
  const [erroEnvio, setErroEnvio] = useState('')
  const [uploadStatus, setUploadStatus] = useState({})
  const [buscandoCep, setBuscandoCep] = useState(false)

  useEffect(() => {
    async function fetchForm() {
      try {
        setLoading(true)
        setError(null)
        const response = await publicApi.get(`/api/cadastro-publico/${slug}`)
        setData(response.data)
      } catch (err) {
        setError(err.response?.status === 404 ? 'not_found' : 'generic')
      } finally {
        setLoading(false)
      }
    }
    fetchForm()
  }, [slug])

  if (loading) return <LoadingSkeleton />
  if (error === 'not_found') return <NotFoundPage />
  if (error) return <ErrorPage />

  const { titulo, subtitulo, banner_url, cor_primaria, campos, perguntas_por_pagina, termos_lgpd_texto, termos_lgpd_obrigatorio } = data
  const paginas = chunk(campos, perguntas_por_pagina)
  const totalSteps = 1 + paginas.length + 1 // capa + páginas de campos + confirmação/LGPD
  const ultimoStep = totalSteps - 1

  const setCampoNativo = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }))
  const setResposta = (itemId, valor) => setForm(prev => ({ ...prev, respostas: { ...prev.respostas, [itemId]: valor } }))

  const valorDoItem = (item) => item.campo_nativo === 'nome' ? form.nome : (form[item.campo_nativo] || '')

  const handleCampoNativoChange = async (item, valorBruto) => {
    const campo = item.campo_nativo
    const mask = MASK_NATIVO[campo]
    const valor = mask ? mask(valorBruto) : valorBruto
    setCampoNativo(campo === 'nome' ? 'nome' : campo, valor)

    if (campo === 'cep') {
      const digits = valor.replace(/\D/g, '')
      if (digits.length === 8) {
        setBuscandoCep(true)
        try {
          const resultado = await fetchCEP(digits)
          if (resultado) {
            setForm(prev => ({
              ...prev,
              endereco: resultado.endereco || prev.endereco,
              bairro: resultado.bairro || prev.bairro,
              cidade: resultado.cidade || prev.cidade,
              estado: resultado.estado || prev.estado,
            }))
          }
        } finally {
          setBuscandoCep(false)
        }
      }
    }
  }

  const validarPagina = (itens) => {
    for (const item of itens) {
      if (!item.obrigatorio) continue
      if (item.origem === 'nativo') {
        if (!valorDoItem(item).trim()) return `Preencha: ${item.label}`
      } else {
        if (!(form.respostas[item.id] || '').toString().trim()) return `Responda: ${item.label}`
      }
    }
    return null
  }

  const avancar = () => {
    const paginaAtual = paginas[step - 1]
    const erro = validarPagina(paginaAtual || [])
    if (erro) { setErroEnvio(erro); return }
    setErroEnvio('')
    setStep(s => Math.min(s + 1, ultimoStep))
  }

  const voltar = () => {
    setErroEnvio('')
    setStep(s => Math.max(s - 1, 0))
  }

  const handleUpload = async (itemId, file) => {
    if (!file) return
    setUploadStatus(prev => ({ ...prev, [itemId]: 'enviando' }))
    try {
      const presign = await publicApi.post(`/api/cadastro-publico/${slug}/upload-anexo`, {
        filename: file.name,
        content_type: file.type,
      })
      const { upload_url, fields, key } = presign.data

      const formData = new FormData()
      Object.entries(fields).forEach(([k, v]) => formData.append(k, v))
      formData.append('file', file)
      await axios.post(upload_url, formData)

      setResposta(itemId, key)
      setUploadStatus(prev => ({ ...prev, [itemId]: 'ok' }))
    } catch {
      setUploadStatus(prev => ({ ...prev, [itemId]: 'erro' }))
    }
  }

  const enviar = async () => {
    if (termos_lgpd_obrigatorio && !form.aceite_lgpd) {
      setErroEnvio('É necessário aceitar os termos para continuar')
      return
    }
    try {
      setEnviando(true)
      setErroEnvio('')
      const response = await publicApi.post(`/api/cadastro-publico/${slug}/enviar`, form)
      setMensagemSucesso(response.data?.mensagem || 'Cadastro realizado com sucesso!')
      setEnviado(true)
    } catch (err) {
      setErroEnvio(err.response?.data?.detail || 'Erro ao enviar cadastro. Tente novamente.')
    } finally {
      setEnviando(false)
    }
  }

  const renderItem = (item) => (
    <div key={item.id}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {item.label}{item.obrigatorio && ' *'}
      </label>

      {item.origem === 'nativo' && (
        <div className="relative">
          <input type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={valorDoItem(item)}
            inputMode={item.campo_nativo === 'cpf_cnpj' || item.campo_nativo === 'telefone' || item.campo_nativo === 'cep' ? 'numeric' : 'text'}
            onChange={(e) => handleCampoNativoChange(item, e.target.value)} />
          {item.campo_nativo === 'cep' && buscandoCep && (
            <Loader2 className="w-4 h-4 animate-spin text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
          )}
        </div>
      )}

      {item.origem === 'customizada' && item.tipo === 'texto' && (
        <input type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          value={form.respostas[item.id] || ''} onChange={(e) => setResposta(item.id, e.target.value)} />
      )}

      {item.origem === 'customizada' && item.tipo === 'multipla_escolha' && (
        <div className="space-y-2">
          {(item.opcoes || []).map((op, idx) => {
            const selecionado = form.respostas[item.id] === op
            return (
              <button
                key={op}
                type="button"
                onClick={() => setResposta(item.id, op)}
                className="w-full flex items-center gap-3 border rounded-lg px-3 py-2.5 text-left text-sm transition-colors"
                style={selecionado
                  ? { borderColor: cor_primaria, backgroundColor: `${cor_primaria}14`, color: cor_primaria }
                  : { borderColor: '#d1d5db' }}
              >
                <span className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs font-bold border"
                  style={selecionado ? { borderColor: cor_primaria, backgroundColor: cor_primaria, color: '#fff' } : { borderColor: '#9ca3af', color: '#6b7280' }}>
                  {selecionado ? <Check size={13} /> : String.fromCharCode(65 + idx)}
                </span>
                <span className="text-gray-800">{op}</span>
              </button>
            )
          })}
        </div>
      )}

      {item.origem === 'customizada' && item.tipo === 'upload' && (
        <div>
          <input type="file" className="text-sm" onChange={(e) => handleUpload(item.id, e.target.files[0])} />
          {uploadStatus[item.id] === 'enviando' && <p className="text-xs text-gray-500 mt-1">Enviando...</p>}
          {uploadStatus[item.id] === 'ok' && <p className="text-xs text-green-600 mt-1">Arquivo enviado ✓</p>}
          {uploadStatus[item.id] === 'erro' && <p className="text-xs text-red-600 mt-1">Erro ao enviar arquivo</p>}
        </div>
      )}
    </div>
  )

  return (
    <div className="min-h-screen flex flex-col bg-slate-900">
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
          {banner_url && (
            <img src={banner_url} alt="" className="w-full h-auto max-h-96 object-contain bg-slate-900" />
          )}
          <div className="px-6 py-5 text-white" style={{ backgroundColor: cor_primaria }}>
            <h1 className="text-xl font-bold">{titulo || 'Cadastro'}</h1>
            {subtitulo && <p className="text-white/85 text-sm mt-1">{subtitulo}</p>}
          </div>

          <div className="p-6">
            {enviado ? (
              <div className="text-center py-8 space-y-3">
                <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: cor_primaria }} />
                <p className="text-gray-700">{mensagemSucesso}</p>
              </div>
            ) : (
              <>
                {step === 0 && (
                  <div className="space-y-4 text-center">
                    <p className="text-gray-600 text-sm">Vamos começar seu cadastro. Leva menos de 2 minutos.</p>
                    <button onClick={() => setStep(1)} className="w-full py-3 rounded-xl text-white font-semibold"
                      style={{ backgroundColor: cor_primaria }}>
                      Começar
                    </button>
                  </div>
                )}

                {step >= 1 && step <= paginas.length && (
                  <div className="space-y-4">
                    {paginas[step - 1].map(renderItem)}
                    {erroEnvio && <p className="text-sm text-red-600">{erroEnvio}</p>}
                    <div className="flex gap-2 pt-2">
                      <button onClick={voltar} className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium">
                        Voltar
                      </button>
                      <button onClick={avancar} className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: cor_primaria }}>
                        Próximo →
                      </button>
                    </div>
                    {paginas.length > 1 && (
                      <p className="text-center text-xs text-gray-400">Página {step} de {paginas.length}</p>
                    )}
                  </div>
                )}

                {step === ultimoStep && (
                  <div className="space-y-4">
                    {termos_lgpd_texto && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                        <p className="text-xs text-gray-600 whitespace-pre-wrap">{termos_lgpd_texto}</p>
                      </div>
                    )}
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" className="mt-0.5" checked={form.aceite_lgpd}
                        onChange={(e) => setForm(prev => ({ ...prev, aceite_lgpd: e.target.checked }))} />
                      Li e aceito os termos acima{termos_lgpd_obrigatorio && ' *'}
                    </label>
                    {erroEnvio && <p className="text-sm text-red-600">{erroEnvio}</p>}
                    <div className="flex gap-2">
                      {paginas.length > 0 && (
                        <button onClick={voltar} className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium">
                          Voltar
                        </button>
                      )}
                      <button onClick={enviar} disabled={enviando}
                        className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                        style={{ backgroundColor: cor_primaria }}>
                        {enviando && <Loader2 className="w-4 h-4 animate-spin" />}
                        {enviando ? 'Enviando...' : 'Finalizar cadastro'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Honeypot anti-bot: invisível para humanos, bots costumam preencher */}
                <input
                  type="text"
                  name="website"
                  value={form.website || ''}
                  onChange={(e) => setCampoNativo('website', e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-700 border-t-blue-500"></div>
      <p className="mt-4 text-slate-400 text-sm">Carregando formulário...</p>
    </div>
  )
}

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center px-4">
      <div className="text-center">
        <div className="text-6xl mb-4">🔗</div>
        <h1 className="text-2xl font-bold text-white mb-2">Formulário não encontrado</h1>
        <p className="text-slate-400 max-w-md">
          Este link de cadastro não existe ou não está disponível no momento.
        </p>
      </div>
    </div>
  )
}

function ErrorPage() {
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center px-4">
      <div className="text-center">
        <div className="text-5xl mb-4">⚠️</div>
        <h1 className="text-xl font-bold text-white mb-2">Erro ao carregar</h1>
        <p className="text-slate-400">Ocorreu um erro. Tente novamente mais tarde.</p>
      </div>
    </div>
  )
}
